import React from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import MaskedInput from 'react-text-mask';
import PropTypes from 'prop-types';

import classes from './style.module.scss';
import ClientLink from '@/components/ClientsPage/ClientLink/ClientLink';
import Card from '@/components/common/Card';
import Radio from '@/components/common/elements/Radio';
import clientImage from '@/components/common/icons/client-circle.svg';
import detailsImage from '@/components/common/icons/client-details.svg';
import contactImage from '@/components/common/icons/contact-circle.svg';
import documentImage from '@/components/common/icons/document-circle.svg';
import infoIcon from '@/components/common/icons/info.svg';
import walletImage from '@/components/common/icons/wallet.svg';
import Spinner from '@/components/common/Spinner/Spinner';
import { USERNAME_MASK } from '@/helpers/mask';
import AddressIcon from '@/svg-icons/address';
import ClientIcon from '@/svg-icons/client';
import Tooltip from '@/components/ui-bridge/core/Tooltip';

const ClientFormComponent = ({
  action,
  busy,
  client,
  clientId,
  intl,
  loading,
  loadingCnpj = false,
  franchiseeCountry,
  showBillingAddress,
  classifications,
  billingModalities,
  billingModalitiesPanel,
  zipcodeMask,
  states,
  cities,
  billingCities,
  neighborhoods,
  billingNeighborhoods,
  status,
  type,
  onChange,
  onSubmit,
  cnpjMask,
  cpfOnlyMask,
  phoneMask,
  cellphoneMask,
  cellphoneMaskLength,
  documentError,
  useTextInputForAddress = false,
}) => {
  // Helper function to render validation errors
  const renderValidationError = error => {
    if (!error) return null;
    return <div className={classes.errorMessage}>{error}</div>;
  };

  return loading ? (
    <div className={classes.clientForm}>
      <div className={classes.spinner}>
        <Spinner />
      </div>
    </div>
  ) : (
    <div className={classes.clientForm}>
      <Card className="fade-in">
        <h2 className={classes.cardHeadWithClientPublicLink}>
          <div className={classes.item}>
            <div className={classes.back}>
              <Link to={action === 'edit' ? `/clients/${status}/${clientId}` : '/clients/enabled'}>
                <div className={classes.arrowLeft} />
              </Link>
            </div>
            <div className={classes.icon}>
              <img src={detailsImage} alt={intl.formatMessage({ id: 'DETAILS' })} />
            </div>
            {action === 'new' ? intl.formatMessage({ id: 'NEW_CLIENT' }) : intl.formatMessage({ id: 'DETAILS' })}
          </div>
          <div className={classes.item}>
            <ClientLink />
          </div>
        </h2>

        <form onSubmit={onSubmit}>
          <h3 className={classes.subtitle}>
            <div className={classes.icon}>
              <img src={clientImage} alt={intl.formatMessage({ id: 'CUSTOMER' })} />
            </div>

            {intl.formatMessage({ id: 'CUSTOMER' })}
          </h3>

          <div className={`${classes.field} ${classes.hasRadio}`}>
            <div className={classes.radio}>
              <Radio
                name="client_type"
                className={classes.radioInput}
                id="clientTypePJ"
                data-cy="clientTypePJ"
                value="pj"
                onChange={onChange}
                checked={type === 'pj'}
                disabled={action === 'edit'}
              />

              <label htmlFor="clientTypePJ">{intl.formatMessage({ id: 'LEGAL_PERSON' })}</label>
            </div>

            <div className={classes.radio}>
              <Radio
                name="client_type"
                className={classes.radioInput}
                id="clientTypePF"
                data-cy="clientTypePF"
                value="pf"
                onChange={onChange}
                checked={type === 'pf'}
                disabled={action === 'edit'}
              />

              <label htmlFor="clientTypePF">{intl.formatMessage({ id: 'NATURAL_PERSON' })}</label>
            </div>
          </div>

          <div className={`${classes.field} ${classes.isFlex}`}>
            {type === 'pj' ? (
              <>
                <div className={`${classes.field} ${classes.is6}`}>
                  <label htmlFor="cnpj">{intl.formatMessage({ id: 'CNPJ' })}*</label>

                  <MaskedInput
                    type="text"
                    name="document"
                    id="cnpj"
                    data-cy="cnpj"
                    required
                    value={client?.document || ''}
                    disabled={action === 'edit' || loadingCnpj}
                    onChange={onChange}
                    guide={false}
                    mask={cnpjMask}
                  />
                  {renderValidationError(documentError)}
                </div>

                <div className={`${classes.field} ${classes.is6}`}>
                  <label htmlFor="billing-name">{intl.formatMessage({ id: 'COMPANY_NAME' })}*</label>

                  <input
                    type="text"
                    name="company_name"
                    id="billing-name"
                    data-cy="billing-name"
                    required
                    value={client?.company_name || ''}
                    disabled={action === 'edit'}
                    onChange={onChange}
                  />
                </div>
              </>
            ) : (
              <div className={`${classes.field} ${classes.is6}`}>
                <label htmlFor="cpf">{intl.formatMessage({ id: 'CPF' })}*</label>

                <MaskedInput
                  type="text"
                  name="document"
                  id="cpf"
                  data-cy="cpf"
                  required
                  value={client?.document || ''}
                  disabled={action === 'edit'}
                  onChange={onChange}
                  guide={false}
                  mask={cpfOnlyMask}
                />
                {renderValidationError(documentError)}
              </div>
            )}
          </div>

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="name">
                {type === 'pj' ? intl.formatMessage({ id: 'FANTASY_NAME' }) : intl.formatMessage({ id: 'NAME' })}*
              </label>

              <input
                type="text"
                name="name"
                id="name"
                data-cy="name"
                required
                value={client?.name || ''}
                onChange={onChange}
                disabled={action === 'edit'}
              />
            </div>

            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="classification">{intl.formatMessage({ id: 'CLASSIFICATION' })}*</label>

              <Select
                name="classification"
                id="classification"
                data-cy="classification"
                className="select-container"
                classNamePrefix="select"
                placeholder=""
                value={
                  classifications.find(classification => classification === client?.classification)
                    ? {
                        label: intl.formatMessage({ id: `CLASSIFICATION.${client.classification}` }),
                        value: client.classification,
                      }
                    : null
                }
                onChange={selectedOption => {
                  const event = {
                    target: {
                      name: 'classification',
                      value: selectedOption ? selectedOption.value : '',
                    },
                  };
                  onChange(event);
                }}
                options={classifications.map(classification => ({
                  label: intl.formatMessage({ id: `CLASSIFICATION.${classification}` }),
                  value: classification,
                }))}
                noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
                required
              />
            </div>
          </div>

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="username">{intl.formatMessage({ id: 'USER' })}*</label>

              <MaskedInput
                type="text"
                name="username"
                id="username"
                data-cy="username"
                required
                value={client?.username || ''}
                disabled={action === 'edit'}
                onChange={onChange}
                guide={false}
                mask={action === 'edit' ? false : USERNAME_MASK}
              />
            </div>

            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="email">{intl.formatMessage({ id: 'EMAIL' })}*</label>

              <input
                type="text"
                name="contact[email]"
                id="email"
                data-cy="email"
                disabled={action === 'edit'}
                required
                value={client?.contact?.email || ''}
                onChange={onChange}
              />
            </div>
          </div>

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="billingModalities">{intl.formatMessage({ id: 'BILLING_MODALITIES' })}*</label>

              <Select
                name="billing_modality"
                id="billingModalities"
                data-cy="billingModalities"
                className="select-container"
                classNamePrefix="select"
                placeholder=""
                value={
                  billingModalities.find(modality => modality === client?.billing_modality)
                    ? {
                        label: intl.formatMessage({ id: `BILLING_MODALITY.${client.billing_modality}` }),
                        value: client.billing_modality,
                      }
                    : null
                }
                onChange={selectedOption => {
                  const event = {
                    target: {
                      name: 'billing_modality',
                      value: selectedOption ? selectedOption.value : '',
                    },
                  };
                  onChange(event);
                }}
                options={billingModalities.map(billingModality => ({
                  label: intl.formatMessage({ id: `BILLING_MODALITY.${billingModality}` }),
                  value: billingModality,
                }))}
                noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
                required
              />
            </div>
            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="modality">
                {intl.formatMessage({ id: 'BILLING_MODALITIES_PANEL' })}*
                <Tooltip title={intl.formatMessage({ id: 'BILLING_MODALITY_TOOLTIP' })} placement="bottom">
                  <img src={infoIcon} alt="Info" />
                </Tooltip>
              </label>

              <Select
                name="modality"
                id="modality"
                data-cy="modality"
                className="select-container"
                classNamePrefix="select"
                placeholder=""
                value={
                  billingModalitiesPanel.find(modality => modality === client?.modality)
                    ? {
                        label: intl.formatMessage({ id: `BILLING_MODALITY.${client.modality}` }),
                        value: client.modality,
                      }
                    : null
                }
                onChange={selectedOption => {
                  const event = {
                    target: {
                      name: 'modality',
                      value: selectedOption ? selectedOption.value : '',
                    },
                  };
                  onChange(event);
                }}
                options={billingModalitiesPanel.map(modality => ({
                  label: intl.formatMessage({ id: `BILLING_MODALITY.${modality}` }),
                  value: modality,
                }))}
                noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
                required
              />
            </div>
          </div>

          <h3 className={classes.subtitle}>
            <div className={classes.icon}>
              <img src={walletImage} alt={intl.formatMessage({ id: 'PROPERTIES_PORTFOLIO' })} />
            </div>

            {intl.formatMessage({ id: 'PROPERTIES_PORTFOLIO' })}
          </h3>

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="properties_purchase_sell">
                {intl.formatMessage({ id: 'PROPERTIES_FOR_PURCHASE_SELL' })}
              </label>

              <input
                type="number"
                name="propertiesWallet[properties_purchase_sell]"
                id="properties_purchase_sell"
                data-cy="properties_purchase_sell"
                min="0"
                value={client?.propertiesWallet?.properties_purchase_sell || ''}
                onChange={onChange}
              />
            </div>

            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="properties_rent">{intl.formatMessage({ id: 'PROPERTIES_FOR_RENT' })}</label>

              <input
                type="number"
                name="propertiesWallet[properties_rent]"
                id="properties_rent"
                data-cy="properties_rent"
                min="0"
                value={client?.propertiesWallet?.properties_rent || ''}
                onChange={onChange}
              />
            </div>
          </div>

          <div className={classes.field}>
            <label htmlFor="potential_inspections">{intl.formatMessage({ id: 'MONTHLY_POTENTIAL_INSPECTIONS' })}</label>

            <input
              type="number"
              name="propertiesWallet[potential_inspections]"
              id="potential_inspections"
              data-cy="potential_inspections"
              min="0"
              value={client?.propertiesWallet?.potential_inspections || ''}
              onChange={onChange}
            />
          </div>

          {type === 'pj' && (
            <>
              <h3 className={classes.subtitle}>
                <div className={classes.icon}>
                  <ClientIcon />
                </div>

                {intl.formatMessage({ id: 'RESPONSIBLE' })}
              </h3>

              <div className={classes.field}>
                <label htmlFor="responsibleName">{intl.formatMessage({ id: 'NAME' })}*</label>

                <input
                  type="text"
                  name="responsible[name]"
                  id="responsibleName"
                  data-cy="responsibleName"
                  value={client?.responsible?.name || ''}
                  onChange={onChange}
                />
              </div>

              <div className={`${classes.field} ${classes.isFlex}`}>
                <div className={`${classes.field} ${classes.is4}`}>
                  <label htmlFor="responsibleDocument">{intl.formatMessage({ id: 'CPF' })}*</label>

                  <MaskedInput
                    type="text"
                    name="responsible[document]"
                    id="responsibleDocument"
                    data-cy="responsibleDocument"
                    required
                    value={client?.responsible?.document || ''}
                    onChange={onChange}
                    guide={false}
                    mask={cpfOnlyMask}
                  />
                </div>

                <div className={`${classes.field} ${classes.is4}`}>
                  <label htmlFor="responsiblePhone">{intl.formatMessage({ id: 'PHONE' })}</label>

                  <MaskedInput
                    type="text"
                    name="responsible[phone]"
                    id="responsiblePhone"
                    data-cy="responsiblePhone"
                    value={client?.responsible?.phone || ''}
                    onChange={onChange}
                    guide={false}
                    mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)}
                  />
                </div>

                <div className={`${classes.field} ${classes.is4}`}>
                  <label htmlFor="responsibleCellphone">{intl.formatMessage({ id: 'CELLPHONE' })}</label>

                  <MaskedInput
                    type="text"
                    name="responsible[cellphone]"
                    id="responsibleCellphone"
                    data-cy="responsibleCellphone"
                    value={client?.responsible?.cellphone || ''}
                    onChange={onChange}
                    guide={false}
                    mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)}
                  />
                </div>
              </div>

              <div className={classes.field}>
                <label htmlFor="ResponsibleEmail">{intl.formatMessage({ id: 'EMAIL' })}</label>

                <input
                  type="text"
                  name="responsible[email]"
                  id="ResponsibleEmail"
                  data-cy="ResponsibleEmail"
                  value={client?.responsible?.email || ''}
                  onChange={onChange}
                />
              </div>
            </>
          )}

          <h3 className={classes.subtitle}>
            <div className={classes.icon}>
              <AddressIcon />
            </div>

            {intl.formatMessage({ id: 'CUSTOMER_ADDRESS' })}
          </h3>

          {franchiseeCountry !== 'CO' && (
            <div className={`${classes.field} ${classes.isFlex}`}>
              <div className={`${classes.field} ${classes.is6}`}>
                <label htmlFor="zipcode">{intl.formatMessage({ id: 'CEP' })}</label>

                <MaskedInput
                  type="text"
                  name="address[zipcode]"
                  id="zipcode"
                  data-cy="zipcode"
                  value={client?.address?.zipcode || ''}
                  onChange={onChange}
                  guide={false}
                  mask={zipcodeMask}
                  required
                />
              </div>
            </div>
          )}

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is2}`}>
              <label htmlFor="state">{intl.formatMessage({ id: 'STATE' })}</label>

              {useTextInputForAddress ? (
                <input
                  type="text"
                  name="address[state]"
                  id="state"
                  data-cy="state"
                  value={client?.address?.state || ''}
                  onChange={onChange}
                  required
                />
              ) : (
                <Select
                  name="address[state_id]"
                  id="state"
                  data-cy="state"
                  className="select-container"
                  classNamePrefix="select"
                  placeholder=""
                  value={(() => {
                    if (!client?.address?.state_id) return null;
                    const foundState = states.find(state => state.id === client.address.state_id);
                    return foundState ? { label: foundState.name, value: foundState.id, id: foundState.id } : null;
                  })()}
                  onChange={selectedOption => {
                    const event = {
                      target: {
                        name: 'address[state_id]',
                        value: selectedOption ? selectedOption.id : '',
                      },
                    };
                    onChange(event);
                  }}
                  options={states.map(state => ({ label: state.name, value: state.id, id: state.id }))}
                  noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
                  required
                />
              )}
            </div>

            <div className={`${classes.field} ${classes.is5}`}>
              <label htmlFor="city">{intl.formatMessage({ id: 'CITY' })}</label>

              {useTextInputForAddress ? (
                <input
                  type="text"
                  name="address[city]"
                  id="city"
                  data-cy="city"
                  value={client?.address?.city || ''}
                  onChange={onChange}
                  required
                />
              ) : (
                <CreatableSelect
                  name="address[city]"
                  id="city"
                  data-cy="city"
                  className="select-container"
                  classNamePrefix="select"
                  placeholder=""
                  value={(() => {
                    if (!client?.address?.city) return null;

                    // Always return the city name as both label and value
                    return { label: client.address.city, value: client.address.city };
                  })()}
                  onChange={selectedOption => {
                    if (!selectedOption) {
                      // Clear city when no option is selected
                      onChange({
                        target: { name: 'address[city]', value: '' },
                      });
                      return;
                    }

                    // Always update with the city name
                    onChange({
                      target: { name: 'address[city]', value: selectedOption.label },
                    });
                  }}
                  options={(() => {
                    const options = cities.map(city => ({ label: city.name, value: city.name }));

                    // If we have a city value that's not in the options, add it as a temporary option
                    if (client?.address?.city && !options.find(opt => opt.label === client.address.city)) {
                      options.unshift({
                        label: client.address.city,
                        value: client.address.city,
                      });
                    }

                    return options;
                  })()}
                  formatCreateLabel={inputValue => `${intl.formatMessage({ id: 'ADD' })} "${inputValue}"`}
                  noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
                  required
                />
              )}
            </div>

            <div className={`${classes.field} ${classes.is5}`}>
              <label htmlFor="neighborhood">{intl.formatMessage({ id: 'NEIGHBORHOOD' })}</label>

              {useTextInputForAddress ? (
                <input
                  type="text"
                  name="address[neighborhood]"
                  id="neighborhood"
                  data-cy="neighborhood"
                  value={client?.address?.neighborhood || ''}
                  onChange={onChange}
                  required
                />
              ) : (
                <CreatableSelect
                  name="address[neighborhood]"
                  id="neighborhood"
                  data-cy="neighborhood"
                  className="select-container"
                  classNamePrefix="select"
                  placeholder=""
                  value={(() => {
                    if (!client?.address?.neighborhood) return null;

                    // Always return the neighborhood name as both label and value
                    return { label: client.address.neighborhood, value: client.address.neighborhood };
                  })()}
                  onChange={selectedOption => {
                    if (!selectedOption) {
                      // Clear neighborhood when no option is selected
                      onChange({
                        target: { name: 'address[neighborhood]', value: '' },
                      });
                      return;
                    }

                    // Always update with the neighborhood name
                    onChange({
                      target: { name: 'address[neighborhood]', value: selectedOption.label },
                    });
                  }}
                  options={(() => {
                    const options = neighborhoods.map(neighborhood => ({
                      label: neighborhood.name,
                      value: neighborhood.name,
                    }));

                    // If we have a neighborhood value that's not in the options, add it as a temporary option
                    if (
                      client?.address?.neighborhood &&
                      !options.find(opt => opt.label === client.address.neighborhood)
                    ) {
                      options.unshift({
                        label: client.address.neighborhood,
                        value: client.address.neighborhood,
                      });
                    }

                    return options;
                  })()}
                  formatCreateLabel={inputValue => `${intl.formatMessage({ id: 'ADD' })} "${inputValue}"`}
                  noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
                  required
                />
              )}
            </div>
          </div>

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="street">{intl.formatMessage({ id: 'STREET' })}</label>

              <input
                type="text"
                name="address[street]"
                id="street"
                data-cy="street"
                value={client?.address?.street || ''}
                onChange={onChange}
                required
              />
            </div>

            <div className={`${classes.field} ${classes.is3}`}>
              <label htmlFor="number">{intl.formatMessage({ id: 'NUMBER' })}</label>

              <input
                type="text"
                name="address[number]"
                id="number"
                data-cy="number"
                value={client?.address?.number || ''}
                onChange={onChange}
                required
              />
            </div>

            <div className={`${classes.field} ${classes.is3}`}>
              <label htmlFor="complement">{intl.formatMessage({ id: 'COMPLEMENT' })}</label>

              <input
                type="text"
                name="address[complement]"
                id="complement"
                data-cy="complement"
                value={client?.address?.complement || ''}
                onChange={onChange}
              />
            </div>
          </div>

          {/* Hidden inputs to maintain IDs for backend submission */}
          <input type="hidden" name="address[state_id]" value={client?.address?.state_id || ''} />
          <input type="hidden" name="address[city_id]" value={client?.address?.city_id || ''} />
          <input type="hidden" name="address[neighborhood_id]" value={client?.address?.neighborhood_id || ''} />

          <h3 className={classes.subtitle}>
            <div className={classes.icon}>
              <AddressIcon />
            </div>

            {intl.formatMessage({ id: 'BILLING_ADDRESS' })}
          </h3>

          <div className={`${classes.field} ${classes.hasRadio}`}>
            <div className={classes.radio}>
              <Radio
                name="showBillingAddress"
                className={classes.radioInput}
                id="showBillingAddressNo"
                data-cy="showBillingAddressNo"
                value="false"
                onChange={onChange}
                checked={showBillingAddress === false}
              />

              <label htmlFor="showBillingAddressNo">{intl.formatMessage({ id: 'SAME_ADDRESS' })}</label>
            </div>

            <div className={classes.radio}>
              <Radio
                name="showBillingAddress"
                className={classes.radioInput}
                id="showBillingAddressYes"
                data-cy="showBillingAddressYes"
                value="true"
                onChange={onChange}
                checked={showBillingAddress === true}
              />

              <label htmlFor="showBillingAddressYes">{intl.formatMessage({ id: 'DIFFERENT_ADDRESS' })}</label>
            </div>
          </div>

          {showBillingAddress && (
            <>
              {franchiseeCountry !== 'CO' && (
                <div className={`${classes.field} ${classes.isFlex}`}>
                  <div className={`${classes.field} ${classes.is6}`}>
                    <label htmlFor="billing_address_zipcode">{intl.formatMessage({ id: 'CEP' })}</label>

                    <MaskedInput
                      type="text"
                      name="billing_address[zipcode]"
                      id="billing_address_zipcode"
                      data-cy="billing_address_zipcode"
                      value={client?.billing_address?.zipcode || ''}
                      onChange={onChange}
                      guide={false}
                      mask={zipcodeMask}
                      required
                    />
                  </div>
                </div>
              )}

              <div className={`${classes.field} ${classes.isFlex}`}>
                <div className={`${classes.field} ${classes.is2}`}>
                  <label htmlFor="billing_address_state_id">{intl.formatMessage({ id: 'STATE' })}</label>

                  {useTextInputForAddress ? (
                    <input
                      type="text"
                      name="billing_address[state]"
                      id="billing_address_state_id"
                      data-cy="billing_address_state_id"
                      value={client?.billing_address?.state || ''}
                      onChange={onChange}
                      required
                    />
                  ) : (
                    <Select
                      name="billing_address[state_id]"
                      id="billing_address_state_id"
                      data-cy="billing_address_state_id"
                      className="select-container"
                      classNamePrefix="select"
                      placeholder=""
                      value={(() => {
                        if (!client?.billing_address?.state_id) return null;
                        const foundState = states.find(state => state.id === client.billing_address.state_id);
                        return foundState ? { label: foundState.name, value: foundState.id, id: foundState.id } : null;
                      })()}
                      onChange={selectedOption => {
                        const event = {
                          target: {
                            name: 'billing_address[state_id]',
                            value: selectedOption ? selectedOption.id : '',
                          },
                        };
                        onChange(event);
                      }}
                      options={states.map(state => ({ label: state.name, value: state.id, id: state.id }))}
                      noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
                      required
                    />
                  )}
                </div>

                <div className={`${classes.field} ${classes.is5}`}>
                  <label htmlFor="billing_address_city_id">{intl.formatMessage({ id: 'CITY' })}</label>

                  {useTextInputForAddress ? (
                    <input
                      type="text"
                      name="billing_address[city]"
                      id="billing_address_city_id"
                      data-cy="billing_address_city_id"
                      value={client?.billing_address?.city || ''}
                      onChange={onChange}
                      required
                    />
                  ) : (
                    <CreatableSelect
                      name="billing_address[city]"
                      id="billing_address_city_id"
                      data-cy="billing_address_city_id"
                      className="select-container"
                      classNamePrefix="select"
                      placeholder=""
                      value={(() => {
                        if (!client?.billing_address?.city) return null;

                        // Always return the city name as both label and value
                        return { label: client.billing_address.city, value: client.billing_address.city };
                      })()}
                      onChange={selectedOption => {
                        if (!selectedOption) {
                          // Clear city when no option is selected
                          onChange({
                            target: { name: 'billing_address[city]', value: '' },
                          });
                          return;
                        }

                        // Always update with the city name
                        onChange({
                          target: { name: 'billing_address[city]', value: selectedOption.label },
                        });
                      }}
                      options={(() => {
                        const options = billingCities.map(city => ({ label: city.name, value: city.name }));

                        // If we have a city value that's not in the options, add it as a temporary option
                        if (
                          client?.billing_address?.city &&
                          !options.find(opt => opt.label === client.billing_address.city)
                        ) {
                          options.unshift({
                            label: client.billing_address.city,
                            value: client.billing_address.city,
                          });
                        }

                        return options;
                      })()}
                      formatCreateLabel={inputValue => `${intl.formatMessage({ id: 'ADD' })} "${inputValue}"`}
                      noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
                      required
                    />
                  )}
                </div>

                <div className={`${classes.field} ${classes.is5}`}>
                  <label htmlFor="billing_address_neighborhood_id">{intl.formatMessage({ id: 'NEIGHBORHOOD' })}</label>

                  {useTextInputForAddress ? (
                    <input
                      type="text"
                      name="billing_address[neighborhood]"
                      id="billing_address_neighborhood_id"
                      data-cy="billing_address_neighborhood_id"
                      value={client?.billing_address?.neighborhood || ''}
                      onChange={onChange}
                      required
                    />
                  ) : (
                    <CreatableSelect
                      name="billing_address[neighborhood]"
                      id="billing_address_neighborhood_id"
                      data-cy="billing_address_neighborhood_id"
                      className="select-container"
                      classNamePrefix="select"
                      placeholder=""
                      value={(() => {
                        if (!client?.billing_address?.neighborhood) return null;

                        // Always return the neighborhood name as both label and value
                        return {
                          label: client.billing_address.neighborhood,
                          value: client.billing_address.neighborhood,
                        };
                      })()}
                      onChange={selectedOption => {
                        if (!selectedOption) {
                          // Clear neighborhood when no option is selected
                          onChange({
                            target: { name: 'billing_address[neighborhood]', value: '' },
                          });
                          return;
                        }

                        // Always update with the neighborhood name
                        onChange({
                          target: { name: 'billing_address[neighborhood]', value: selectedOption.label },
                        });
                      }}
                      options={(() => {
                        const options = billingNeighborhoods.map(neighborhood => ({
                          label: neighborhood.name,
                          value: neighborhood.name,
                        }));

                        // If we have a neighborhood value that's not in the options, add it as a temporary option
                        if (
                          client?.billing_address?.neighborhood &&
                          !options.find(opt => opt.label === client.billing_address.neighborhood)
                        ) {
                          options.unshift({
                            label: client.billing_address.neighborhood,
                            value: client.billing_address.neighborhood,
                          });
                        }

                        return options;
                      })()}
                      formatCreateLabel={inputValue => `${intl.formatMessage({ id: 'ADD' })} "${inputValue}"`}
                      noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
                      required
                    />
                  )}
                </div>
              </div>

              <div className={`${classes.field} ${classes.isFlex}`}>
                <div className={`${classes.field} ${classes.is6}`}>
                  <label htmlFor="billing_address_street">{intl.formatMessage({ id: 'STREET' })}</label>

                  <input
                    type="text"
                    name="billing_address[street]"
                    id="billing_address_street"
                    data-cy="billing_address_street"
                    value={client?.billing_address?.street || ''}
                    onChange={onChange}
                    required
                  />
                </div>

                <div className={`${classes.field} ${classes.is3}`}>
                  <label htmlFor="billing_address_number">{intl.formatMessage({ id: 'NUMBER' })}</label>

                  <input
                    type="text"
                    name="billing_address[number]"
                    id="billing_address_number"
                    data-cy="billing_address_number"
                    value={client?.billing_address?.number || ''}
                    onChange={onChange}
                    required
                  />
                </div>

                <div className={`${classes.field} ${classes.is3}`}>
                  <label htmlFor="billing_address_complement">{intl.formatMessage({ id: 'COMPLEMENT' })}</label>

                  <input
                    type="text"
                    name="billing_address[complement]"
                    id="billing_address_complement"
                    data-cy="billing_address_complement"
                    value={client?.billing_address?.complement || ''}
                    onChange={onChange}
                  />
                </div>
              </div>

              {/* Hidden inputs to maintain IDs for backend submission */}
              <input type="hidden" name="billing_address[state_id]" value={client?.billing_address?.state_id || ''} />
              <input type="hidden" name="billing_address[city_id]" value={client?.billing_address?.city_id || ''} />
              <input
                type="hidden"
                name="billing_address[neighborhood_id]"
                value={client?.billing_address?.neighborhood_id || ''}
              />
            </>
          )}

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is4}`}>
              <label htmlFor="billing_contact">{intl.formatMessage({ id: 'BILLING_CONTACT' })}</label>

              <input
                type="text"
                name="billing_contact"
                id="billing_contact"
                data-cy="billing_contact"
                value={client?.billing_contact || ''}
                onChange={onChange}
              />
            </div>

            <div className={`${classes.field} ${classes.is4}`}>
              <label htmlFor="billing_email">{intl.formatMessage({ id: 'BILLING_EMAIL' })}</label>

              <input
                type="email"
                name="billing_email"
                id="billing_email"
                data-cy="billing_email"
                value={client?.billing_email || ''}
                onChange={onChange}
              />
            </div>

            <div className={`${classes.field} ${classes.is4}`}>
              <label htmlFor="billing_phone">{intl.formatMessage({ id: 'BILLING_PHONE' })}</label>

              <MaskedInput
                type="text"
                name="billing_phone"
                id="billing_phone"
                data-cy="billing_phone"
                value={client?.billing_phone || ''}
                onChange={onChange}
                guide={false}
                mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)}
              />
            </div>
          </div>

          <h3 className={classes.subtitle}>
            <div className={classes.icon}>
              <img src={contactImage} alt={intl.formatMessage({ id: 'CONTACT' })} />
            </div>

            {intl.formatMessage({ id: 'CONTACT' })}
          </h3>

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is12}`}>
              <label htmlFor="assignmentEmailRule">
                {intl.formatMessage({ id: 'EMAIL_FOR_ORDER_ASSIGNMENT_RULE' })}
              </label>

              <Select
                id="assignmentEmailRule"
                name="contact[assignment_email_rule]"
                data-cy="assignmentEmailRule"
                className="select-container"
                classNamePrefix="select"
                placeholder=""
                value={
                  client?.contact?.assignment_email_rule
                    ? {
                        label: intl.formatMessage({
                          id: `ORDER_ASSIGNMENT_RULE_${client.contact.assignment_email_rule}`,
                        }),
                        value: client.contact.assignment_email_rule,
                      }
                    : null
                }
                onChange={selectedOption => {
                  const event = {
                    target: {
                      name: 'contact[assignment_email_rule]',
                      value: selectedOption ? selectedOption.value : '',
                    },
                  };
                  onChange(event);
                }}
                options={[
                  { label: intl.formatMessage({ id: 'ORDER_ASSIGNMENT_RULE_CREATOR' }), value: 'CREATOR' },
                  { label: intl.formatMessage({ id: 'ORDER_ASSIGNMENT_RULE_CUSTOM' }), value: 'CUSTOM' },
                  { label: intl.formatMessage({ id: 'ORDER_ASSIGNMENT_RULE_RESPONSIBLE' }), value: 'RESPONSIBLE' },
                  { label: intl.formatMessage({ id: 'ORDER_ASSIGNMENT_RULE_NONE' }), value: 'NONE' },
                ]}
                noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
              />
            </div>
          </div>

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is12}`}>
              <label htmlFor="assignmentEmail">{intl.formatMessage({ id: 'EMAIL_FOR_ORDER_ASSIGNMENT' })}</label>

              <input
                id="assignmentEmail"
                type="text"
                name="contact[assignment_email]"
                data-cy="assignmentEmail"
                disabled={client?.contact?.assignment_email_rule !== 'CUSTOM'}
                value={client?.contact?.assignment_email || ''}
                onChange={onChange}
              />
              <p className="pt-1 text-[10px]">{intl.formatMessage({ id: 'EMAIL_SEPARATOR_DESCRIPTION' })}</p>
            </div>
          </div>

          <div className={`${classes.field} ${classes.isFlex}`}>
            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="phone">{intl.formatMessage({ id: 'PHONE' })}</label>

              <MaskedInput
                type="text"
                name="contact[phone]"
                id="phone"
                data-cy="phone"
                value={client?.contact?.phone || ''}
                onChange={onChange}
                guide={false}
                mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)}
                required
              />
            </div>

            <div className={`${classes.field} ${classes.is6}`}>
              <label htmlFor="cellphone">{intl.formatMessage({ id: 'CELLPHONE' })}</label>

              <MaskedInput
                type="text"
                name="contact[cellphone]"
                id="cellphone"
                data-cy="cellphone"
                value={client?.contact?.cellphone || ''}
                onChange={onChange}
                guide={false}
                mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)}
              />
            </div>
          </div>

          <h3 className={classes.subtitle}>
            <div className={classes.icon}>
              <img src={documentImage} alt={intl.formatMessage({ id: 'ORDER_PAGE' })} />
            </div>

            {intl.formatMessage({ id: 'ORDER_PAGE' })}
          </h3>

          <div className={classes.field}>
            <label htmlFor="expiration_time">
              {intl.formatMessage({ id: 'DEADLINE_FOR_RECORDING_DIVERGENCES_IN_DAYS' })}
            </label>

            <input
              type="number"
              name="hotsite_configuration[expiration_time]"
              id="expiration_time"
              data-cy="expiration_time"
              min="0"
              value={client?.hotsite_configuration?.expiration_time || ''}
              onChange={onChange}
            />
          </div>

          <div className={classes.field}>
            <label htmlFor="cc_email">{intl.formatMessage({ id: 'ADMINISTRATOR_EMAIL' })}</label>

            <input
              type="email"
              name="hotsite_configuration[cc_email]"
              id="cc_email"
              data-cy="cc_email"
              value={client?.hotsite_configuration?.cc_email || ''}
              onChange={onChange}
            />
          </div>

          <div className={classes.field}>
            <label htmlFor="canRegisterMaintenances">
              {intl.formatMessage({ id: 'REPAIR_AND_ALTERATION_SOLICITATIONS' })}
            </label>

            <Select
              id="canRegisterMaintenances"
              name="hotsite_configuration[can_register_maintenances]"
              data-cy="canRegisterMaintenances"
              className="select-container"
              classNamePrefix="select"
              placeholder=""
              value={
                client?.hotsite_configuration?.can_register_maintenances !== undefined
                  ? {
                      label: intl.formatMessage({
                        id: client.hotsite_configuration.can_register_maintenances
                          ? 'AVAILABLE_IF_SUPPORTED_BY_PLAN'
                          : 'DISABLED_REPEATED',
                      }),
                      value: client.hotsite_configuration.can_register_maintenances,
                    }
                  : { label: intl.formatMessage({ id: 'AVAILABLE_IF_SUPPORTED_BY_PLAN' }), value: true }
              }
              onChange={selectedOption => {
                const event = {
                  target: {
                    name: 'hotsite_configuration[can_register_maintenances]',
                    value: selectedOption ? selectedOption.value : true,
                  },
                };
                onChange(event);
              }}
              options={[
                { label: intl.formatMessage({ id: 'AVAILABLE_IF_SUPPORTED_BY_PLAN' }), value: true },
                { label: intl.formatMessage({ id: 'DISABLED_REPEATED' }), value: false },
              ]}
              noOptionsMessage={() => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' })}
            />
          </div>

          <div className={classes.cardFooter}>
            <Link to={`/clients/${status}/${clientId}`} className={classes.button} disabled={busy}>
              {intl.formatMessage({ id: 'CANCEL' })}
            </Link>

            <button type="submit" className={`${classes.button} ${classes.isGreen}`} disabled={busy}>
              {intl.formatMessage({ id: 'SAVE' })}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

ClientFormComponent.propTypes = {
  action: PropTypes.string.isRequired,
  busy: PropTypes.bool.isRequired,
  client: PropTypes.object,
  clientId: PropTypes.string,
  intl: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  loadingCnpj: PropTypes.bool,
  showBillingAddress: PropTypes.bool,
  classifications: PropTypes.array.isRequired,
  billingModalities: PropTypes.array.isRequired,
  billingModalitiesPanel: PropTypes.array.isRequired,
  zipcodeMask: PropTypes.array.isRequired,
  states: PropTypes.array.isRequired,
  cities: PropTypes.array.isRequired,
  billingCities: PropTypes.array.isRequired,
  neighborhoods: PropTypes.array.isRequired,
  billingNeighborhoods: PropTypes.array.isRequired,
  status: PropTypes.string,
  type: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cnpjMask: PropTypes.array.isRequired,
  cpfOnlyMask: PropTypes.array.isRequired,
  phoneMask: PropTypes.array.isRequired,
  cellphoneMask: PropTypes.array.isRequired,
  cellphoneMaskLength: PropTypes.number.isRequired,
  franchiseeCountry: PropTypes.string.isRequired,
  documentError: PropTypes.string,
  useTextInputForAddress: PropTypes.bool,
};

ClientFormComponent.defaultProps = {
  client: null,
  clientId: null,
  type: null,
  status: null,
  documentError: '',
  useTextInputForAddress: false,
};

export default ClientFormComponent;
